import { useState, useEffect, useRef, useCallback } from 'react';
import {
  getNotificationSettings,
  setNotificationSettings,
} from 'api/cansurvey/surveyApi';
import type { NotificationSettings } from 'types/survey';

interface UseNotificationSettingsReturn {
  settings: NotificationSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  save: (enabled: boolean, emails: string[]) => Promise<void>;
  saving: boolean;
}

export const useNotificationSettings = (
  surveyId: number | null
): UseNotificationSettingsReturn => {
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const mountedRef = useRef(true);

  const fetchSettings = useCallback(async () => {
    if (surveyId === null) return;

    setLoading(true);
    setError(null);
    try {
      const data = await getNotificationSettings(surveyId);
      if (mountedRef.current) {
        setSettings(data);
      }
    } catch (err) {
      console.error('[useNotificationSettings] Failed to fetch:', err);
      if (mountedRef.current) {
        setError(
          err instanceof Error ? err.message : 'Failed to load notification settings'
        );
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [surveyId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchSettings();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchSettings]);

  const save = useCallback(
    async (enabled: boolean, emails: string[]) => {
      if (surveyId === null) return;
      setSaving(true);
      try {
        await setNotificationSettings(surveyId, enabled, emails);
        if (mountedRef.current) {
          setSettings({ enabled, emails });
        }
      } finally {
        if (mountedRef.current) {
          setSaving(false);
        }
      }
    },
    [surveyId]
  );

  return { settings, loading, error, refetch: fetchSettings, save, saving };
};
